import {
  Badge,
  Box,
  Button,
  Collapsible,
  Drawer,
  HStack,
  Input,
  Portal,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Edit2,
  Globe,
  Lock,
  Save,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { mqttProfilesApi } from '@/lib/api'
import type {
  MqttConnectionProfile,
  MqttProtocol,
  MqttQoS,
  MqttVersion,
  UpdateMqttProfileRequest,
} from '@/lib/mqtt-types'
import { useColorModeValue } from '~/color-mode'
import { toaster } from '~/toaster'

const DEFAULT_PORTS_MAP: Record<MqttProtocol, number> = {
  mqtt: 1883,
  mqtts: 8883,
  ws: 8080,
  wss: 8884,
}

interface ProfileManagerDrawerProps {
  isOpen: boolean
  onClose: () => void
}

interface EditFormState {
  name: string
  protocol: MqttProtocol
  host: string
  port: number
  path: string
  username: string
  password: string
  clientIdPrefix: string
  tlsInsecure: boolean
  mqttVersion: MqttVersion
  cleanSession: boolean
  keepAlive: number
  lwTopic: string
  lwMessage: string
  lwQos: MqttQoS
  lwRetain: boolean
}

function profileToFormState(profile: MqttConnectionProfile): EditFormState {
  return {
    name: profile.name,
    protocol: profile.protocol,
    host: profile.host,
    port: profile.port,
    path: profile.path || '/mqtt',
    username: profile.username || '',
    password: '',
    clientIdPrefix: profile.clientIdPrefix || 'stories',
    tlsInsecure: profile.tlsInsecure,
    mqttVersion: profile.mqttVersion,
    cleanSession: profile.cleanSession,
    keepAlive: profile.keepAlive,
    lwTopic: profile.lwTopic || '',
    lwMessage: profile.lwMessage || '',
    lwQos: profile.lwQos,
    lwRetain: profile.lwRetain,
  }
}

export function ProfileManagerDrawer({ isOpen, onClose }: ProfileManagerDrawerProps) {
  const queryClient = useQueryClient()
  const { hasMinRole } = useAuth()
  const isAdmin = hasMinRole('admin')
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null)
  const [editingIsPublic, setEditingIsPublic] = useState(false)
  const [editForm, setEditForm] = useState<EditFormState | null>(null)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [deleteIsPublic, setDeleteIsPublic] = useState(false)
  const warningColor = useColorModeValue('#D16503', '#FB8A3B')

  // Fetch profiles
  const { data: profilesData, isLoading } = useQuery({
    queryKey: ['mqtt-profiles'],
    queryFn: () => mqttProfilesApi.list(),
  })

  const profiles = profilesData?.profiles || []

  // Update mutation (for private profiles)
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMqttProfileRequest }) =>
      mqttProfilesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mqtt-profiles'] })
      setEditingProfileId(null)
      setEditingIsPublic(false)
      setEditForm(null)
      toaster.success({
        title: 'Profile Updated',
        description: 'Connection profile updated successfully',
      })
    },
    onError: (error: Error) => {
      toaster.error({
        title: 'Update Failed',
        description: error.message,
      })
    },
  })

  // Update mutation for public profiles (admin only)
  const updatePublicMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMqttProfileRequest }) =>
      mqttProfilesApi.updatePublic(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mqtt-profiles'] })
      setEditingProfileId(null)
      setEditingIsPublic(false)
      setEditForm(null)
      toaster.success({
        title: 'Public Profile Updated',
        description: 'Public connection profile updated successfully',
      })
    },
    onError: (error: Error) => {
      toaster.error({
        title: 'Update Failed',
        description: error.message,
      })
    },
  })

  // Delete mutation (for private profiles)
  const deleteMutation = useMutation({
    mutationFn: (id: string) => mqttProfilesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mqtt-profiles'] })
      setDeleteConfirmId(null)
      setDeleteIsPublic(false)
      toaster.success({
        title: 'Profile Deleted',
        description: 'Connection profile deleted successfully',
      })
    },
    onError: (error: Error) => {
      toaster.error({
        title: 'Delete Failed',
        description: error.message,
      })
    },
  })

  // Delete mutation for public profiles (admin only)
  const deletePublicMutation = useMutation({
    mutationFn: (id: string) => mqttProfilesApi.deletePublic(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mqtt-profiles'] })
      setDeleteConfirmId(null)
      setDeleteIsPublic(false)
      toaster.success({
        title: 'Public Profile Deleted',
        description: 'Public connection profile deleted successfully',
      })
    },
    onError: (error: Error) => {
      toaster.error({
        title: 'Delete Failed',
        description: error.message,
      })
    },
  })

  // Reset state when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setEditingProfileId(null)
      setEditingIsPublic(false)
      setEditForm(null)
      setShowAdvanced(false)
      setDeleteConfirmId(null)
      setDeleteIsPublic(false)
    }
  }, [isOpen])

  const handleStartEdit = (profile: MqttConnectionProfile) => {
    setEditingProfileId(profile.id)
    setEditingIsPublic(profile.isPublic)
    setEditForm(profileToFormState(profile))
    setShowAdvanced(false)
  }

  const handleCancelEdit = () => {
    setEditingProfileId(null)
    setEditingIsPublic(false)
    setEditForm(null)
    setShowAdvanced(false)
  }

  const handleSaveEdit = () => {
    if (!editingProfileId || !editForm) return

    const data: UpdateMqttProfileRequest = {
      name: editForm.name,
      protocol: editForm.protocol,
      host: editForm.host,
      port: editForm.port,
      path: editForm.path || undefined,
      username: editForm.username || undefined,
      password: editForm.password || undefined,
      clientIdPrefix: editForm.clientIdPrefix || undefined,
      tlsInsecure: editForm.tlsInsecure,
      mqttVersion: editForm.mqttVersion,
      cleanSession: editForm.cleanSession,
      keepAlive: editForm.keepAlive,
      lwTopic: editForm.lwTopic || undefined,
      lwMessage: editForm.lwMessage || undefined,
      lwQos: editForm.lwQos,
      lwRetain: editForm.lwRetain,
    }

    if (editingIsPublic) {
      updatePublicMutation.mutate({ id: editingProfileId, data })
    } else {
      updateMutation.mutate({ id: editingProfileId, data })
    }
  }

  const handleProtocolChange = (protocol: MqttProtocol) => {
    if (!editForm) return
    setEditForm((prev) =>
      prev
        ? {
            ...prev,
            protocol,
            port: DEFAULT_PORTS_MAP[protocol],
          }
        : null,
    )
  }

  const handleDelete = (id: string, isPublic: boolean) => {
    if (isPublic) {
      deletePublicMutation.mutate(id)
    } else {
      deleteMutation.mutate(id)
    }
  }

  const handleStartDelete = (profile: MqttConnectionProfile) => {
    setDeleteConfirmId(profile.id)
    setDeleteIsPublic(profile.isPublic)
  }

  // Check if user can edit/delete a profile
  const canModifyProfile = (profile: MqttConnectionProfile): boolean => {
    if (profile.isPublic) {
      return isAdmin
    }
    return true // User can always modify their own private profiles
  }

  return (
    <Drawer.Root placement="end" open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="md">
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <Drawer.Header borderBottomWidth="1px">
              <HStack justify="space-between" w="100%">
                <Text fontWeight="bold" fontSize="lg">
                  Manage Connection Profiles
                </Text>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <X size={20} />
                </Button>
              </HStack>
            </Drawer.Header>
            <Drawer.Body>
              {isLoading ? (
                <Box py={8} textAlign="center" color="content.secondary">
                  <Text>Loading profiles...</Text>
                </Box>
              ) : profiles.length === 0 ? (
                <Box py={8} textAlign="center" color="content.secondary">
                  <Text fontSize="sm">No saved profiles</Text>
                  <Text fontSize="xs" mt={1}>
                    Connect to a broker and click the save button to create a profile
                  </Text>
                </Box>
              ) : (
                <VStack gap={4} align="stretch">
                  {profiles.map((profile) => (
                    <Box
                      key={profile.id}
                      p={4}
                      borderWidth="1px"
                      borderRadius="md"
                      bg={editingProfileId === profile.id ? 'brand.subtle' : 'bg.panel'}
                    >
                      {editingProfileId === profile.id && editForm ? (
                        // Edit Mode
                        <VStack gap={3} align="stretch">
                          {/* Profile Name */}
                          <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={1}>
                              Profile Name
                            </Text>
                            <Input
                              size="sm"
                              value={editForm.name}
                              onChange={(e) =>
                                setEditForm((prev) =>
                                  prev ? { ...prev, name: e.target.value } : null,
                                )
                              }
                              placeholder="Profile name"
                            />
                          </Box>

                          {/* Protocol */}
                          <Box>
                            <Text fontSize="sm" mb={1}>
                              Protocol
                            </Text>
                            <HStack gap={1}>
                              {(['wss', 'ws', 'mqtts', 'mqtt'] as MqttProtocol[]).map(
                                (protocol) => (
                                  <Button
                                    key={protocol}
                                    size="sm"
                                    variant={editForm.protocol === protocol ? 'solid' : 'outline'}
                                    colorPalette={
                                      editForm.protocol === protocol ? 'brand' : undefined
                                    }
                                    onClick={() => handleProtocolChange(protocol)}
                                  >
                                    {protocol.toUpperCase()}
                                  </Button>
                                ),
                              )}
                            </HStack>
                          </Box>

                          {/* Host & Port */}
                          <HStack gap={2}>
                            <Box flex={3}>
                              <Text fontSize="sm" mb={1}>
                                Host
                              </Text>
                              <Input
                                size="sm"
                                value={editForm.host}
                                onChange={(e) =>
                                  setEditForm((prev) =>
                                    prev ? { ...prev, host: e.target.value } : null,
                                  )
                                }
                                placeholder="broker.hivemq.com"
                              />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="sm" mb={1}>
                                Port
                              </Text>
                              <Input
                                size="sm"
                                type="number"
                                value={editForm.port}
                                onChange={(e) =>
                                  setEditForm((prev) =>
                                    prev ? { ...prev, port: parseInt(e.target.value) || 0 } : null,
                                  )
                                }
                              />
                            </Box>
                          </HStack>

                          {/* Path (for WebSocket) */}
                          {(editForm.protocol === 'ws' || editForm.protocol === 'wss') && (
                            <Box>
                              <Text fontSize="sm" mb={1}>
                                Path
                              </Text>
                              <Input
                                size="sm"
                                value={editForm.path}
                                onChange={(e) =>
                                  setEditForm((prev) =>
                                    prev ? { ...prev, path: e.target.value } : null,
                                  )
                                }
                                placeholder="/mqtt"
                              />
                            </Box>
                          )}

                          {/* Username & Password */}
                          <HStack gap={2}>
                            <Box flex={1}>
                              <Text fontSize="sm" mb={1}>
                                Username
                              </Text>
                              <Input
                                size="sm"
                                value={editForm.username}
                                onChange={(e) =>
                                  setEditForm((prev) =>
                                    prev ? { ...prev, username: e.target.value } : null,
                                  )
                                }
                                placeholder="Optional"
                              />
                            </Box>
                            <Box flex={1}>
                              <Text fontSize="sm" mb={1}>
                                Password
                              </Text>
                              <Input
                                size="sm"
                                type="password"
                                value={editForm.password}
                                onChange={(e) =>
                                  setEditForm((prev) =>
                                    prev ? { ...prev, password: e.target.value } : null,
                                  )
                                }
                                placeholder="Leave empty to keep"
                              />
                            </Box>
                          </HStack>

                          {/* TLS Insecure */}
                          {(editForm.protocol === 'mqtts' || editForm.protocol === 'wss') && (
                            <HStack justify="space-between">
                              <HStack gap={2}>
                                {editForm.tlsInsecure && (
                                  <AlertTriangle size={14} color={warningColor} />
                                )}
                                <Text fontSize="sm">Ignore Certificate Errors</Text>
                              </HStack>
                              <Switch.Root
                                checked={editForm.tlsInsecure}
                                onCheckedChange={(e) =>
                                  setEditForm((prev) =>
                                    prev ? { ...prev, tlsInsecure: e.checked } : null,
                                  )
                                }
                              >
                                <Switch.HiddenInput />
                                <Switch.Control />
                              </Switch.Root>
                            </HStack>
                          )}

                          {/* Advanced Options */}
                          <Box>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowAdvanced(!showAdvanced)}
                              w="100%"
                              justifyContent="flex-start"
                            >
                              {showAdvanced ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                              <Text ml={1}>Advanced Options</Text>
                            </Button>

                            <Collapsible.Root open={showAdvanced}>
                              <Collapsible.Content>
                                <VStack gap={3} align="stretch" mt={2} pl={4}>
                                  {/* Client ID Prefix */}
                                  <Box>
                                    <Text fontSize="sm" mb={1}>
                                      Client ID Prefix
                                    </Text>
                                    <Input
                                      size="sm"
                                      value={editForm.clientIdPrefix}
                                      onChange={(e) =>
                                        setEditForm((prev) =>
                                          prev ? { ...prev, clientIdPrefix: e.target.value } : null,
                                        )
                                      }
                                      placeholder="stories"
                                    />
                                  </Box>

                                  {/* MQTT Version */}
                                  <Box>
                                    <Text fontSize="sm" mb={1}>
                                      MQTT Version
                                    </Text>
                                    <HStack gap={1}>
                                      {([5, 3] as MqttVersion[]).map((version) => (
                                        <Button
                                          key={version}
                                          size="sm"
                                          variant={
                                            editForm.mqttVersion === version ? 'solid' : 'outline'
                                          }
                                          onClick={() =>
                                            setEditForm((prev) =>
                                              prev ? { ...prev, mqttVersion: version } : null,
                                            )
                                          }
                                        >
                                          MQTT {version === 5 ? '5.0' : '3.1.1'}
                                        </Button>
                                      ))}
                                    </HStack>
                                  </Box>

                                  {/* Clean Session & Keep Alive */}
                                  <HStack gap={4}>
                                    <HStack>
                                      <Text fontSize="sm">Clean Session</Text>
                                      <Switch.Root
                                        checked={editForm.cleanSession}
                                        onCheckedChange={(e) =>
                                          setEditForm((prev) =>
                                            prev ? { ...prev, cleanSession: e.checked } : null,
                                          )
                                        }
                                      >
                                        <Switch.HiddenInput />
                                        <Switch.Control />
                                      </Switch.Root>
                                    </HStack>
                                    <Box flex={1}>
                                      <Text fontSize="sm" mb={1}>
                                        Keep Alive (s)
                                      </Text>
                                      <Input
                                        size="sm"
                                        type="number"
                                        value={editForm.keepAlive}
                                        onChange={(e) =>
                                          setEditForm((prev) =>
                                            prev
                                              ? {
                                                  ...prev,
                                                  keepAlive: parseInt(e.target.value) || 60,
                                                }
                                              : null,
                                          )
                                        }
                                      />
                                    </Box>
                                  </HStack>

                                  {/* Last Will */}
                                  <Box borderTopWidth="1px" pt={2}>
                                    <Text fontSize="sm" fontWeight="medium" mb={2}>
                                      Last Will & Testament
                                    </Text>
                                    <VStack gap={2} align="stretch">
                                      <Input
                                        size="sm"
                                        placeholder="LW Topic"
                                        value={editForm.lwTopic}
                                        onChange={(e) =>
                                          setEditForm((prev) =>
                                            prev ? { ...prev, lwTopic: e.target.value } : null,
                                          )
                                        }
                                      />
                                      <Input
                                        size="sm"
                                        placeholder="LW Message"
                                        value={editForm.lwMessage}
                                        onChange={(e) =>
                                          setEditForm((prev) =>
                                            prev ? { ...prev, lwMessage: e.target.value } : null,
                                          )
                                        }
                                      />
                                      <HStack gap={2}>
                                        <Box flex={1}>
                                          <Text fontSize="xs" mb={1}>
                                            QoS
                                          </Text>
                                          <HStack gap={1}>
                                            {([0, 1, 2] as MqttQoS[]).map((qos) => (
                                              <Button
                                                key={qos}
                                                size="xs"
                                                variant={
                                                  editForm.lwQos === qos ? 'solid' : 'outline'
                                                }
                                                onClick={() =>
                                                  setEditForm((prev) =>
                                                    prev ? { ...prev, lwQos: qos } : null,
                                                  )
                                                }
                                              >
                                                {qos}
                                              </Button>
                                            ))}
                                          </HStack>
                                        </Box>
                                        <HStack>
                                          <Text fontSize="xs">Retain</Text>
                                          <Switch.Root
                                            checked={editForm.lwRetain}
                                            onCheckedChange={(e) =>
                                              setEditForm((prev) =>
                                                prev ? { ...prev, lwRetain: e.checked } : null,
                                              )
                                            }
                                          >
                                            <Switch.HiddenInput />
                                            <Switch.Control />
                                          </Switch.Root>
                                        </HStack>
                                      </HStack>
                                    </VStack>
                                  </Box>
                                </VStack>
                              </Collapsible.Content>
                            </Collapsible.Root>
                          </Box>

                          {/* Save/Cancel Buttons */}
                          <HStack gap={2} pt={2}>
                            <Button
                              colorPalette="brand"
                              onClick={handleSaveEdit}
                              loading={updateMutation.isPending}
                              disabled={!editForm.name || !editForm.host}
                              flex={1}
                            >
                              <Save size={16} />
                              Save Changes
                            </Button>
                            <Button
                              variant="outline"
                              onClick={handleCancelEdit}
                              disabled={updateMutation.isPending}
                            >
                              Cancel
                            </Button>
                          </HStack>
                        </VStack>
                      ) : deleteConfirmId === profile.id ? (
                        // Delete Confirmation
                        <VStack gap={3} align="stretch">
                          <Text fontWeight="medium">Delete "{profile.name}"?</Text>
                          {deleteIsPublic && (
                            <Text fontSize="sm" color="orange.600" _dark={{ color: 'orange.400' }}>
                              This is a public profile. Deleting it will affect all users.
                            </Text>
                          )}
                          <Text fontSize="sm" color="content.secondary">
                            This action cannot be undone.
                          </Text>
                          <HStack gap={2}>
                            <Button
                              colorPalette="red"
                              onClick={() => handleDelete(profile.id, deleteIsPublic)}
                              loading={deleteMutation.isPending || deletePublicMutation.isPending}
                              flex={1}
                            >
                              <Trash2 size={16} />
                              Delete
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setDeleteConfirmId(null)
                                setDeleteIsPublic(false)
                              }}
                              disabled={deleteMutation.isPending || deletePublicMutation.isPending}
                            >
                              Cancel
                            </Button>
                          </HStack>
                        </VStack>
                      ) : (
                        // View Mode
                        <HStack justify="space-between" align="start">
                          <VStack align="start" gap={1} flex={1}>
                            <HStack gap={2}>
                              <Text fontWeight="medium">{profile.name}</Text>
                              {profile.isPublic ? (
                                <Badge size="sm" colorPalette="green" variant="subtle">
                                  <Globe size={10} />
                                  <Text ml={1}>Public</Text>
                                </Badge>
                              ) : (
                                <Badge size="sm" colorPalette="gray" variant="subtle">
                                  <Lock size={10} />
                                  <Text ml={1}>Private</Text>
                                </Badge>
                              )}
                            </HStack>
                            <HStack gap={2} flexWrap="wrap">
                              <Badge size="sm" colorPalette="brand" variant="subtle">
                                {profile.protocol.toUpperCase()}
                              </Badge>
                              <Text fontSize="xs" color="content.secondary">
                                {profile.host}:{profile.port}
                              </Text>
                              {profile.username && (
                                <Badge size="sm" variant="subtle">
                                  Auth
                                </Badge>
                              )}
                              {profile.tlsInsecure && (
                                <Badge size="sm" colorPalette="yellow" variant="subtle">
                                  Insecure TLS
                                </Badge>
                              )}
                            </HStack>
                          </VStack>
                          <HStack gap={1}>
                            {canModifyProfile(profile) ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleStartEdit(profile)}
                                  title="Edit profile"
                                >
                                  <Edit2 size={16} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  colorPalette="red"
                                  onClick={() => handleStartDelete(profile)}
                                  title="Delete profile"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </>
                            ) : (
                              <Text fontSize="xs" color="content.secondary" fontStyle="italic">
                                Admin only
                              </Text>
                            )}
                          </HStack>
                        </HStack>
                      )}
                    </Box>
                  ))}
                </VStack>
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}

export default ProfileManagerDrawer
